import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { catchErrorMessage } from 'utility/helpers';
import { toast } from 'react-toastify';
import {
  invalidateProjectQueries,
  ProjectType,
  useProjectsQuery,
} from 'pages/projects/hooks/useProjectsQuery';
import Search from 'pages/knowledge/components/SearchComponent';
import { ProjectsAPI } from '@buildwise/libs/projects';
import useUserData from 'hooks/useUserData';
import { Virtuoso } from 'react-virtuoso';
import { styled } from '@mui/material/styles';
import { Switch } from '@mui/material';
import { $Dialog, $DialogTitle, $DialogContent } from 'components/modals/addPostModal/AddPostModal';
import MapPinLineIcon from 'remixicon-react/MapPinLineIcon';
import CheckLineIcon from 'remixicon-react/CheckLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import _, { debounce } from 'lodash';
import HandleOperation from 'components/utils/HandleOperation';

const $YellowCheckbox = styled(Checkbox, { label: 'YellowCheckbox' })({
  padding: 0,
  '&.Mui-checked': {
    color: 'yellow',
  },
});

const $CheckboxIcon = styled(Box, { label: 'CheckboxIcon' })({
  width: 24,
  height: 24,
  borderRadius: 4,
  border: '1px solid #8993A4',
});

const $CheckboxIconChecked = styled(Box, { label: 'CheckboxIconChecked' })({
  width: 24,
  height: 24,
  borderRadius: 4,
  background: '#FFC107',
  color: '#6D5303',
});

export const $ProjectTitle = styled(Typography, { label: 'ProjectTitle' })(({ theme }) => ({
  fontWeight: 600,
  fontSize: 18,
  lineHeight: '21px',
  letterSpacing: '0.25px',
  color: '#000000',
  marginBottom: 4,
  cursor: 'pointer',
  display: 'inline-flex',
  '&.is-small': {
    fontSize: 16,
    color: theme.palette.custom.body,
    fontWeight: 600,
    lineHeight: '19px',
    marginBottom: 0,
  },
}));

export const $DotBreak = styled('span', { label: 'DotBreak' })(({ theme }) => ({
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  color: theme.palette.custom.label,
  marginLeft: 4,
  marginRight: 4,
  '&.is-small': {
    lineHeight: '14px',
  },
}));

export const $ProjectInfo = styled(Typography, { label: 'ProjectInfo' })(({ theme }) => ({
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  color: theme.palette.custom.label,
  marginBottom: 4,
  display: 'flex',
  alignItems: 'center',
  '&.is-small': {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '14px',
  },
  '& > svg': {
    marginRight: 2,
  },
  '&.darker': {
    color: theme.palette.custom.activeTitle,
    '&.is-small': {
      color: theme.palette.custom.label,
    },
  },
}));

type LinkProjectsModelTypes = {
  linkProjectsModal: boolean;
  setLinkProjectsModal: Dispatch<SetStateAction<boolean>>;
  projectsSelected: ProjectType[];
};

interface IUpdatedProject {
  isUpdated: boolean;
  isSelected: boolean;
  userIds: string[];
}

const LinkProjectsModal: React.FC<LinkProjectsModelTypes> = ({
  linkProjectsModal,
  setLinkProjectsModal,
  projectsSelected,
}) => {
  const { companyId, user, refetchUserData } = useUserData();
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [updatedProjects, setUpdatedProjects] = useState<Record<string, IUpdatedProject>>(
    projectsSelected.reduce((updatedProjects, project) => {
      updatedProjects[project.id] = {
        isUpdated: false,
        isSelected: true,
        userIds: [...(project?.userIds || [])],
      };
      return updatedProjects;
    }, {}),
  );
  const {
    projects,
    isLoading,
    searchInfo: { isLoading: searchLoading },
  } = useProjectsQuery({ searchKeyword });

  const isProjectSelected = (project: ProjectType) => {
    return updatedProjects[project.id]
      ? updatedProjects[project.id].isSelected
      : project?.userIds?.find((id) => id === user?.id);
  };

  const projectsData = useMemo(() => {
    if (showSelectedOnly) {
      return _.uniqBy([...projects, ...projectsSelected], 'id').filter((project) =>
        isProjectSelected(project),
      );
    } else {
      return projects;
    }
  }, [projects, showSelectedOnly]);

  const handleChange = (project: ProjectType) => {
    if (updatedProjects[project.id]?.isUpdated) {
      setUpdatedProjects((prevState) => {
        const updatedState = { ...prevState };
        const updatedUserIds = updatedState[project.id].isSelected
          ? updatedState[project.id].userIds.filter((id) => id !== user?.id)
          : [...updatedState[project.id].userIds, user?.id || ''];
        updatedState[project.id] = {
          isUpdated: !updatedState[project.id].isUpdated,
          isSelected: !updatedState[project.id].isSelected,
          userIds: updatedUserIds,
        };
        return { ...updatedState };
      });
    } else {
      setUpdatedProjects((prevState) => {
        const updatedState = { ...prevState };
        const checked = updatedProjects[project.id]
          ? !updatedProjects[project.id].isSelected
          : !project?.userIds?.find((id) => id === user?.id);
        let userIds = [
          ...(updatedProjects[project.id]
            ? updatedProjects[project.id].userIds
            : project?.userIds?.map((id) => id) || []),
        ];
        if (checked) {
          userIds.push(user?.id || '');
        } else {
          userIds = userIds.filter((id) => id !== user?.id);
        }
        updatedState[project.id] = {
          isUpdated: true,
          isSelected: checked,
          userIds,
        };
        return { ...updatedState };
      });
    }
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      const updateProjectPromises = Object.keys(updatedProjects)
        .filter((id) => updatedProjects[id].isUpdated)
        .map((id) => {
          return new Promise(async (resolve, reject) => {
            try {
              await ProjectsAPI.editProject(companyId, id, {
                userIds: updatedProjects[id].userIds,
              });
              resolve({ success: true, project: updatedProjects[id] });
            } catch (error) {
              console.error('LINK_PROJECT_ERROR', error);
              reject({ success: false, project: updatedProjects[id], error });
            }
          });
        });
      await Promise.all(updateProjectPromises);
      invalidateProjectQueries(companyId);
      toast.success('Projects Updated Successfully.');
      refetchUserData?.();
      handleClose();
    } catch (error) {
      console.error('LINK_PROJECTS_ERROR', error);
      toast.error(catchErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUpdatedProjects({});
    setLinkProjectsModal(false);
  };

  const updateSearch = (value: string) => {
    setSearchKeyword(value.trim());
  };
  const debounceFn = useCallback(debounce(updateSearch, 300), []);

  const anyProjectSelected = () => {
    return projectsData.find((project) =>
      updatedProjects[project.id]
        ? updatedProjects[project.id].isSelected
        : project?.userIds?.find((id) => id === user?.id),
    );
  };

  const anyProjectUpdated = () => {
    return projectsData.find((project) => updatedProjects[project.id]?.isUpdated);
  };

  return (
    <$Dialog
      sx={{ '& .MuiPaper-root': { width: 620, height: '100%' } }}
      onClose={handleClose}
      open={linkProjectsModal}
      fullWidth
    >
      <$DialogTitle sx={{ fontSize: 32, display: 'flex', alignItems: 'center' }}>
        Link Projects
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.custom.lightBlack,
          }}
        >
          <CloseLineIcon size={32} />
        </IconButton>
      </$DialogTitle>

      <$DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 0,
        }}
        dividers
      >
        <Box sx={{ width: '100%', px: 3, py: 1 }}>
          <Box sx={{ pt: 1 }}>
            <Search
              value={searchKeyword}
              placeholder="Search for projects"
              setValue={debounceFn}
              filled
              triggerOnEnter
            />
          </Box>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  disableRipple
                  sx={{ m: 1 }}
                  checked={showSelectedOnly}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setShowSelectedOnly(e.target.checked)
                  }
                />
              }
              label="Show Selected Only"
            />
          </Box>
        </Box>

        <Box sx={{ width: '100%', px: 4 }}>
          {!isLoading && !searchLoading && searchKeyword && projectsData.length === 0 && (
            <p>
              No projects found with search "<b>{searchKeyword}</b>"
            </p>
          )}

          {showSelectedOnly && !anyProjectSelected() && projectsSelected.length === 0 && (
            <p>No projects are selected.</p>
          )}
        </Box>

        <Box
          sx={{
            overflow: 'auto',
            height: '100%',
            width: '100%',
          }}
        >
          <HandleOperation isLoading={isLoading || searchLoading}>
            {projectsData.length > 0 && (
              <Virtuoso
                increaseViewportBy={250}
                style={{
                  height: '100%',
                  width: '100%',
                  padding: '0 25px',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }}
                data={projectsData}
                itemContent={(_, project) => (
                  <Box
                    key={project.id}
                    pb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      pt: 1,
                      px: 0.5,
                      mr: 6,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#EFF0F7', borderRadius: '5px', transition: '0.3s' },
                    }}
                    borderBottom={1}
                    borderColor="#D9DBE9"
                    onClick={() => handleChange(project)}
                  >
                    <Box display="flex">
                      <Avatar
                        sx={{ bgcolor: '#B6E8FC', mr: 2, width: '50px', height: '50px' }}
                        variant="rounded"
                      >
                        <BuildingLineIcon color="black" />
                      </Avatar>
                      <Box>
                        <$ProjectTitle>{project.name}</$ProjectTitle>
                        <Box display="flex">
                          <$ProjectInfo title="Project Number" className={`darker}`}>
                            {project.project_number}
                          </$ProjectInfo>
                          <$ProjectInfo title="Project Type">
                            {project.project_number && project.project_type && (
                              <$DotBreak>•</$DotBreak>
                            )}
                            {project.project_type}
                          </$ProjectInfo>
                          <$ProjectInfo title="Project Location">
                            {(project.project_number || project.project_type) && project.city && (
                              <$DotBreak>•</$DotBreak>
                            )}
                            {project.city && <MapPinLineIcon size={16} />}
                            {project.city}
                          </$ProjectInfo>
                        </Box>
                      </Box>
                    </Box>
                    <$YellowCheckbox
                      checked={Boolean(isProjectSelected(project))}
                      icon={<$CheckboxIcon />}
                      checkedIcon={
                        <$CheckboxIconChecked>
                          <CheckLineIcon />
                        </$CheckboxIconChecked>
                      }
                    />
                  </Box>
                )}
              />
            )}
          </HandleOperation>
        </Box>
      </$DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button
          disabled={loading || !Boolean(anyProjectUpdated())}
          onClick={onSubmit}
          variant="contained"
        >
          {loading ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </$Dialog>
  );
};

export default LinkProjectsModal;
